import { useState, useEffect } from 'react';
import { Flex, Button, Text, Alert, AlertIcon, AlertDescription } from '@chakra-ui/react';
import {
  UploadActions,
  UploadButtonSection,
  UploadExample,
  UploadComplete,
  ModalColumns,
  AcknowledgeBox,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';
import Requirements from './requirements';
import UploadIntro from '../../../atoms/upload-intro';
import sponsorshipLetterExample from '../../../../assets/images/sample-birth-certificate.jpg';
import { BirthCertificateDoc } from '../../../../types';
import getHighlightedErrorSections from './getHighlightedErrorSections';

interface IProps {
  document?: BirthCertificateDoc;
  onContinue: () => void;
  onUpload: (metaData: any) => void;
  onClose: () => void;
  onAddRequiredDocument: (matadata?: any) => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string, infoOnlyRequirementsOption?: string, metadata?: any) => void;
}

const BirthCertificateModal = ({
  document,
  onContinue,
  onUpload,
  onClose,
  onAddRequiredDocument,
  docKey,
  infoOnly,
  docStatus,
  onAcknowledgeToggle
}: IProps) => {
  const { isLegalDocument, isInEnglish, hasProvidedTranslation } = document;

  const questionsComplete =
    isLegalDocument !== undefined &&
    isInEnglish !== undefined &&
    (hasProvidedTranslation !== undefined || (isInEnglish === true && hasProvidedTranslation === undefined));

  const [state, setState] = useState({
    infoOnlyRequirementsOption: document.infoOnlyRequirementsOption,
    questionsComplete,
    highlightedErrorSections: [],
    hasErrors: false,
    hasLetter: undefined,
    hasLetterError: '',
    isLegalDocument: undefined,
    isLegalDocumentError: '',
    isInEnglish: undefined,
    isInEnglishError: '',
    hasProvidedTranslation: undefined,
    hasProvidedTranslationError: '',
    addTranslatedDocAction: false
  });

  useEffect(() => {
    if (state.questionsComplete && document) {
      const highlightedErrorSections = getHighlightedErrorSections(document);

      setState(prevState => ({
        ...prevState,
        highlightedErrorSections,
        hasErrors: document.isDocumentFixed ? false : highlightedErrorSections.length > 0
      }));
    }
  }, [state.questionsComplete, document]);

  const onQuestionButtonClick = (question: IQuestion, stateName: string) => {
    switch (stateName) {
      case 'infoOnlyRequirementsOption':
        setState(prevState => ({
          ...prevState,
          infoOnlyRequirementsOption: question.label === 'Read a list of requirements' ? 'list' : 'checklist'
        }));
        toggleAcknowledge('', false);
        break;
      default:
        setState(prevState => ({
          ...prevState,
          [stateName]: question.label === 'Yes'
        }));
    }
  };

  const isFormValid = () => {
    const { isLegalDocument, isInEnglish, hasProvidedTranslation } = state;

    const isLegalDocumentError = isLegalDocument === undefined ? 'Please select an option' : '';
    const isInEnglishError = isInEnglish === undefined ? 'Please select an option' : '';
    const hasProvidedTranslationError =
      isInEnglish === false && hasProvidedTranslation === undefined ? 'Please select a value' : '';

    if (isLegalDocumentError || isInEnglishError || hasProvidedTranslationError) {
      setState(prevState => ({
        ...prevState,
        isLegalDocumentError,
        isInEnglishError,
        hasProvidedTranslationError
      }));
      return false;
    }
    return true;
  };

  const shouldRequestTranslatedDoc = (highlightedErrorSections: string[]) => {
    let requiresTranslation = false;
    ['2.1', '2.2', '2.3', '2.4', '2.5'].forEach(sectionToShow => {
      if (!requiresTranslation && highlightedErrorSections.includes(sectionToShow)) {
        requiresTranslation = true;
      }
    });
    return requiresTranslation;
  };

  const shouldUploadNewDoc = (highlightedErrorSections: string[]) => {
    let requiresNewDoc = false;
    ['1.1'].forEach(sectionToShow => {
      if (!requiresNewDoc && highlightedErrorSections.includes(sectionToShow)) {
        requiresNewDoc = true;
      }
    });
    return requiresNewDoc;
  };

  const onContinueClick = () => {
    const isFormValidValue = isFormValid();
    const highlightedErrorSections = getHighlightedErrorSections(state);
    const addTranslatedDocAction = shouldRequestTranslatedDoc(highlightedErrorSections);

    setState(prevState => ({
      ...prevState,
      highlightedErrorSections,
      hasErrors: !isFormValidValue,
      questionsComplete: isFormValidValue,
      addTranslatedDocAction
    }));

    if (isFormValidValue && !infoOnly) {
      handleOnUpload();
    }

    if (infoOnly) {
      setTimeout(() => window.scrollTo(0, 0), 150); // timeout added for Chrome issue
    }

    if (addTranslatedDocAction) {
      onAddRequiredDocument(getMetaData());
    } else if (infoOnly) {
      onAcknowledgeToggle('NOT_ACKNOWLEDGED', state.infoOnlyRequirementsOption, getMetaData());
    }
  };

  const onFixUpload = () => {
    setState(prevState => ({
      ...prevState,
      highlightedErrorSections: [],
      hasErrors: false
    }));
    handleOnUpload(true);
  };

  const onUploadAfterApproved = () => {
    const metaData = {
      isLegalDocument: true,
      isInEnglishError: true,
      hasProvidedTranslationError: true,
      hasErrors: false,
      questionsComplete: true,
      highlightedErrorSections: []
    };
    onUpload(metaData);
  };

  const getMetaData = (isFix?: boolean) => {
    const { isLegalDocument, isInEnglish, hasProvidedTranslation, highlightedErrorSections } = state;

    return {
      isLegalDocument,
      isInEnglish,
      hasProvidedTranslation: isInEnglish === false ? hasProvidedTranslation === true : undefined,
      shouldShowRequirements: highlightedErrorSections.length > 0,
      isDocumentFixed: !!isFix
    };
  };

  const handleOnUpload = (isFix?: boolean) => {
    onUpload(getMetaData(isFix));
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      onAcknowledgeToggle('NOT_ACKNOWLEDGED', state.infoOnlyRequirementsOption);
    }
    onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED', state.infoOnlyRequirementsOption);
    }
  };

  const renderQuestions = () => {
    const {
      isLegalDocument,
      isLegalDocumentError,
      isInEnglish,
      isInEnglishError,
      hasProvidedTranslation,
      hasProvidedTranslationError
    } = state;

    return (
      <>
        <RadioOptionsWithConfirm
          id="isLegalDocument"
          label="Are you uploading a scan of the original legal document?"
          errorText={isLegalDocumentError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: isLegalDocument === true
            },
            {
              char: 'N',
              label: 'No',
              selected: isLegalDocument === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => onQuestionButtonClick(question, 'isLegalDocument')}
        />

        <RadioOptionsWithConfirm
          id="isInEnglish"
          label="Is the document in English?"
          errorText={isInEnglishError}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: isInEnglish === true
            },
            {
              char: 'N',
              label: 'No',
              selected: isInEnglish === false
            }
          ]}
          isInline={true}
          onClick={(question: IQuestion) => onQuestionButtonClick(question, 'isInEnglish')}
        />

        {isInEnglish === false && (
          <RadioOptionsWithConfirm
            id="hasProvidedTranslation"
            label="Have you provided an English translation of the birth certificate? "
            errorText={hasProvidedTranslationError}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasProvidedTranslation === true
              },
              {
                char: 'N',
                label: 'No',
                selected: hasProvidedTranslation === false
              }
            ]}
            isInline={true}
            onClick={(question: IQuestion) => onQuestionButtonClick(question, 'hasProvidedTranslation')}
          />
        )}

        <Flex justifyContent="end">
          <Button id="birth-certificate-continue-button" onClick={onContinueClick} variant="solid" size="lg">
            Continue
          </Button>
        </Flex>
      </>
    );
  };

  const renderContent = () => {
    const {
      questionsComplete,
      hasErrors,
      highlightedErrorSections,
      hasLetter,
      hasLetterError,
      infoOnlyRequirementsOption
    } = state;

    let content = null;
    console.log('aaaaaaa');
    console.log(docStatus);
    console.log(hasErrors);
    console.log(questionsComplete);

    if ((hasLetter === false && !questionsComplete) || (docStatus !== 'AWAITING' && hasErrors)) {
      content = (
        <>
          {hasErrors ? (
            <UploadIntro uploadWarning>
              Your document <b>does not meet all requirements</b> therefore further action is required.
            </UploadIntro>
          ) : (
            <Text>Please ensure your document meets the following requirements.</Text>
          )}

          <Requirements uploadComplete={docStatus !== 'AWAITING'} highlightedSections={highlightedErrorSections} />

          {hasErrors && shouldUploadNewDoc(highlightedErrorSections) && (
            <>
              <Text key="what-do-I-have-to-do">
                You will need to <b>upload a new document</b> after you have fixed the issues with the current document.
              </Text>

              <UploadButtonSection onUpload={onFixUpload} buttonLabelText={'Upload new document'}>
                Awaiting upload of <b>New Birth Certificate</b>
              </UploadButtonSection>
            </>
          )}

          {hasErrors && shouldRequestTranslatedDoc(highlightedErrorSections) && (
            <>
              <Alert status="info" key="translated-birth-certificate" mb={6}>
                <AlertIcon />
                <AlertDescription>
                  <b>Translated birth certificate</b> has been added to your list of documents
                </AlertDescription>
              </Alert>
            </>
          )}

          <UploadActions
            complete={!hasErrors || (docStatus === 'REPLACED' && document?.isLegalDocument === true)}
            onContinue={onClose}
            label="Return to document upload"
          />
        </>
      );
    } else if (docStatus !== 'AWAITING' && hasErrors === false && questionsComplete) {
      content = <UploadComplete fileNames={document?.fileNames} onUpload={onFixUpload} onContinue={onClose} />;
    } else if (docStatus === 'APPROVED') {
      content = (
        <UploadComplete fileNames={document?.fileNames} onUpload={onUploadAfterApproved} onContinue={onClose} />
      );
    } else if (docStatus === 'UPLOADED_OK') {
      content = (
        <>
          <UploadButtonSection onUpload={onFixUpload} buttonLabelText={'Add additional files'}>
            Uploaded by University
          </UploadButtonSection>

          <UploadActions complete={true} onContinue={handleOnClose} label="Finish" />
        </>
      );
    }

    return (
      <>
        {infoOnly ? (
          <>
            <Text>
              During your visa application you may be required to provide a birth certificate to connect you to your
              parent or guardian
            </Text>

            <RadioOptionsWithConfirm
              id="infoOnlyRequiremnentsOption"
              label="There are two ways you can check the requirements of this document"
              questions={[
                {
                  char: '1',
                  label: 'Read a list of requirements',
                  selected: infoOnlyRequirementsOption === 'list'
                },
                {
                  char: '2',
                  label: 'Use our online document checklist',
                  selected: infoOnlyRequirementsOption === 'checklist'
                }
              ]}
              onClick={(question: IQuestion) => onQuestionButtonClick(question, 'infoOnlyRequirementsOption')}
            />

            {infoOnlyRequirementsOption === 'list' ? (
              <>
                <Requirements uploadComplete={false} highlightedSections={[]} />

                <AcknowledgeBox
                  id={docKey}
                  blurb="Please confirm you have read and understand the above."
                  isTicked={docStatus === 'ACKNOWLEDGED'}
                  onChange={toggleAcknowledge}
                />
              </>
            ) : null}

            {infoOnlyRequirementsOption === 'checklist' ? (
              <>
                {!questionsComplete ? (
                  <>{renderQuestions()}</>
                ) : (
                  <>
                    {state.highlightedErrorSections.length ? (
                      <>
                        <Requirements
                          uploadComplete={questionsComplete}
                          highlightedSections={state.highlightedErrorSections}
                        />

                        {shouldRequestTranslatedDoc(highlightedErrorSections) && (
                          <Alert status="info" key="translated-birth-certificate" mb={6}>
                            <AlertIcon />

                            <AlertDescription>
                              <b>Translated birth certificate</b> has been added to your list of documents
                            </AlertDescription>
                          </Alert>
                        )}
                      </>
                    ) : (
                      <>
                        <Alert status="success" mb={6}>
                          <AlertIcon />

                          <AlertDescription>
                            Based on the responses provided by you, your document appears to meet requirements at this
                            point in time.
                          </AlertDescription>
                        </Alert>

                        <Text>
                          If you would like to check the requirements of this document manually, you can select "read a
                          list of requirements" in the above options.
                        </Text>
                      </>
                    )}

                    <AcknowledgeBox
                      id={docKey}
                      blurb="Please confirm you have read and understand the above."
                      isTicked={docStatus === 'ACKNOWLEDGED'}
                      onChange={toggleAcknowledge}
                    />
                  </>
                )}
              </>
            ) : null}

            {infoOnlyRequirementsOption === 'list' ||
            (infoOnlyRequirementsOption === 'checklist' && questionsComplete) ? (
              <UploadActions
                complete={docStatus === 'ACKNOWLEDGED'}
                onContinue={handleOnClose}
                label="Return to document upload"
              />
            ) : null}
          </>
        ) : (
          <>
            {!questionsComplete && docStatus === 'AWAITING' ? (
              <>
                <Text>We need a birth certificate to connect you to your parent or guardian.</Text>

                <RadioOptionsWithConfirm
                  id="hasLetter"
                  label="Do you have a document to upload?"
                  errorText={hasLetterError}
                  questions={[
                    {
                      char: 'Y',
                      label: 'Yes',
                      selected: hasLetter === true
                    },
                    {
                      char: 'N',
                      label: 'Not yet',
                      selected: hasLetter === false
                    }
                  ]}
                  isInline={true}
                  onClick={(question: IQuestion) => onQuestionButtonClick(question, 'hasLetter')}
                />
              </>
            ) : (
              docStatus === 'AWAITING' && (
                <>
                  <UploadButtonSection onUpload={() => handleOnUpload()}>
                    Awaiting upload of <b>Birth Certificate</b>
                  </UploadButtonSection>

                  <UploadActions complete={false} onContinue={onContinue} />
                </>
              )
            )}

            {hasLetter === true && !questionsComplete && docStatus === 'AWAITING' ? <>{renderQuestions()}</> : null}

            {content}
          </>
        )}
      </>
    );
  };

  const render = () => {
    return (
      <ModalColumns
        id={'birthCertificate'}
        title={'Birth Certificate'}
        onClose={handleOnClose}
        supportingColumn={<UploadExample altText="Birth certificate example" url={sponsorshipLetterExample} />}
      >
        {renderContent()}
      </ModalColumns>
    );
  };

  return render();
};

export default BirthCertificateModal;
